import React, { useState, useEffect, useCallback, useRef } from "react";
import { API_CONFIG, getHeaders, refreshAccessToken } from "../../config/api";
import { useLoader } from "../../context/loadercontext";
import { RiDeleteBin5Line } from "react-icons/ri";
import { MdEditSquare } from "react-icons/md";
import { ChevronDown, ChevronUp } from "lucide-react";
import toast from "react-hot-toast";
import { DeletePreviewModal, FinalDeleteConfirmModal } from "../common/ui/deletemodal";
import ActionDropdown from "../common/ui/actiondropdown";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext"; // Auth context
import NoFound from "../common/ui/NoFound";
import WarningModal from "../common/ui/warningmodel";

export default function Exbondviewtable({ searchTerm = "", bestartDate = "", beendDate = "", selectedCustomer = "" }) {
  const [openRowIndex, setOpenRowIndex] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [showDeletePreview, setShowDeletePreview] = useState(false);
  const [showFinalDeleteConfirm, setShowFinalDeleteConfirm] = useState(false);
  const [deleteMode, setDeleteMode] = useState(null);
  const [pendingDeleteData, setPendingDeleteData] = useState(null);
  const { setLoading } = useLoader();
  const enableToggle = true;
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [warningVisible, setWarningVisible] =useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  const colorPairs = [
    { bg: "#edeff1", text: "#1c2a3d" },
    { bg: "#dbf6e5", text: "#25915a" },
    { bg: "#fff1d6", text: "#ce880d" },
    { bg: "#ffe4de", text: "#b71d18" },
    { bg: "#d6f4f9", text: "#1a7da7" },
  ];


  const fetchController = useRef(null);
  const fetchExbondData = useCallback(async () => {
    setLoading(true);
    if (fetchController.current) fetchController.current.abort();
    fetchController.current = new AbortController();
    const signal = fetchController.current.signal;

    try {
      const url = new URL(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.EXBOND.GET_ALL}`);

      // Append search filters
      if (searchTerm.trim() !== "") url.searchParams.append("number", searchTerm);
      if (bestartDate) url.searchParams.append("start_date", bestartDate);
      if (beendDate) url.searchParams.append("end_date", beendDate);
      if (selectedCustomer) url.searchParams.append("customer_name", selectedCustomer);

      const callApi = async () =>
        await fetch(url.toString(), {
          method: "GET",
          headers: getHeaders(true),
          signal,
        });

      // Initial API call
      let response = await callApi();

      // -------------------------
      // 🔥 Handle 401 token expired
      // -------------------------
      if (response.status === 401) {
        console.warn("Access token expired. Attempting refresh...");
        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry the same API call
          response = await callApi();
        } else {
          console.error("Refresh token failed. Logging out...");
          logout();
          return;
        }
      }


      const result = await response.json();

      if (result.status === 200 && Array.isArray(result.data)) {
        const mappedData = result.data.map((item) => ({
          id: item.id,
          exbondNumber: item.exbond_id,
          total_duty_exbond_amount_inr: item.total_duty_exbond_amount_inr ? Number(item.total_duty_exbond_amount_inr).toFixed(2) : "0",
          total_weight: item.total_weight ? Number(item.total_weight).toFixed(2) : "0",
          // total_invoice_amount_inr: item.total_invoice_amount_inr ? Number(item.total_invoice_amount_inr).toLocaleString() : "0",
          history: Array.isArray(item.child)
            ? item.child.map((c) => ({
              id: c.id,
              biNumber: c.bi_number,
              beNumber: c.be_number,
              be_date: c.be_date,
              section: c.section_name,
              type: c.type,
              resultant: c.resultant,
              material_name: c.material_name,
              material_short_code: c.material_short_code,
              dutyExBond: Number(c.duty_exbond_amount_inr),
              dollar: Number(c.dollar_inr),
              weight: Number(c.weight),
              invoiceValue: Number(c.invoice_amount_inr),
              rate: Number(c.rate),
              customer: c.customer_name,
              colorIndex: Math.floor(Math.random() * colorPairs.length),
              dutyPaid: c.duty_paid ? "Yes" : "No",
            }))
            : [],
        }));
        setTableData(mappedData);
      } else {
        setTableData([]);
      }
    } catch (error) {
      console.error("Error fetching Exbond data:", error);
      setTableData([]);
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  }, [searchTerm, bestartDate, beendDate, selectedCustomer, setLoading, colorPairs.length, logout]);


  const handleConfirmDelete = async () => {
    if (!pendingDeleteData) return;

    setLoading(true);
    try {
      if (deleteMode === "parent") {
        const exbondId = pendingDeleteData.parent.id;
        let response = await fetch(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.EXBOND.SOFT_DELETE_COMPLETE(exbondId)}`,
          { method: "PUT", headers: getHeaders(true) }
        );

        // -------------------------
        // 🔥 If ACCESS TOKEN expired
        // -------------------------
        if (response.status === 401) {
          console.warn("Access token expired. Attempting refresh...");

          const newToken = await refreshAccessToken();

          if (newToken) {
            // Retry API call with new token
            response = await fetch(
              `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.EXBOND.SOFT_DELETE_COMPLETE(exbondId)}`,
              { method: "PUT", headers: getHeaders(true) }
            );
          } else {
            console.error("Refresh token failed. Logging out...");
            logout(); // redirect user
            return;
          }
        }

        const result = await response.json().catch(() => ({}));

        if (response.ok) {
          if (result.status === 200) toast.success(result.message || "Exbond deleted successfully");
          else if (result.status === 204) toast("Exbond already deleted", { icon: "⚠️" });
         else if (result.status === 400) {
  setWarningMessage(result.message || "Cannot delete — entry exists");
  setWarningVisible(true);
}

        } 
      } else if (deleteMode === "child") {
        const childId = pendingDeleteData.child.id;
        let response = await fetch(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.EXBOND.SOFT_DELETE_PARTIAL(childId)}`,
          { method: "PUT", headers: getHeaders(true) }
        );

        // -------------------------
        // 🔥 If ACCESS TOKEN expired
        // -------------------------
        if (response.status === 401) {
          console.warn("Access token expired. Attempting refresh...");

          const newToken = await refreshAccessToken();

          if (newToken) {
            // Retry API call with new token
            response = await fetch(
              `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.EXBOND.SOFT_DELETE_PARTIAL(childId)}`,
              { method: "PUT", headers: getHeaders(true) }
            );
          } else {
            console.error("Refresh token failed. Logging out...");
            logout(); // redirect user
            return;
          }
        }

        const result = await response.json().catch(() => ({}));

        if (response.ok) {
          if (result.status === 200) toast.success(result.message || "Child deleted successfully");
        else if (result.status === 400) {
  setWarningMessage(result.message || "Cannot delete — entry exists");
  setWarningVisible(true);
}

        } 
      }

      // Refresh table
      await fetchExbondData();
    } catch (error) {
      console.error("Delete error:", error);
  

    } finally {
      setLoading(false);
      setShowFinalDeleteConfirm(false);
      setPendingDeleteData(null);
      setDeleteMode(null);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchExbondData();
    }, 300); // debounce

    return () => clearTimeout(timer);
  }, [searchTerm, bestartDate, beendDate, selectedCustomer, fetchExbondData]);


  const handleToggle = (idx) => setOpenRowIndex(openRowIndex === idx ? null : idx);

  const handleEdit = (parentId, childId) => {
    navigate("/ex-bond/edit-ex-bond", {
      state: { parentId, childId }
    });
  };

  const handleDeleteParent = (id) => {
    const parent = tableData.find((row) => row.id === id);
    if (!parent) return;
    setPendingDeleteData({ parent });
    setDeleteMode("parent");
    setShowDeletePreview(true);
    setShowFinalDeleteConfirm(false);
  };

  const handleDeleteChild = (parentId, childId) => {
    const parent = tableData.find((row) => row.id === parentId);
    if (!parent) return;
    const child = parent.history.find((c) => c.id === childId);
    if (!child) return;
    setPendingDeleteData({ parent, child });
    setDeleteMode("child");
    setShowDeletePreview(true);
    setShowFinalDeleteConfirm(false);
  };

  const handleCancelPreview = () => {
    setShowDeletePreview(false);
    setPendingDeleteData(null);
    setDeleteMode(null);
  };

  const handleProceedToFinalConfirm = () => {
    setShowDeletePreview(false);
    setShowFinalDeleteConfirm(true);
  };
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };
  const formatIndianNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return "0";
    return Number(num).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };


  return (
    <>
      <div className="mt-6 overflow-x-auto">
        <table className="w-full text-[13px] text-center border-collapse">
          <thead className="border-b border-gray-200 text-gray-600 font-medium">
            <tr>
              {enableToggle && <th className="w-12"></th>}
              <th className="px-4 py-3">Exbond No.</th>
              <th className="px-4 py-3">Total Exbond Weight (kg)</th>
              <th className="px-4 py-3">Total Duty Ex-bond (₹)</th>
              {/* <th className="px-4 py-3">Total Invoice Value (₹)</th> */}
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>

          <tbody>
            {tableData.length === 0 ? (
              <tr>
                <td colSpan={10}>
                  <NoFound />
                </td>
              </tr>
            ) : (
              tableData.map((row, idx) => (
                <React.Fragment key={idx}>
                  {/* Parent Row */}
                  <tr
                    className="cursor-pointer transition-colors bg-white hover:bg-gray-50"
                    onClick={() => enableToggle && handleToggle(idx)}
                  >
                    {enableToggle && (
                      <td className="px-3 py-2 text-center">
                        <div className="p-1 rounded hover:bg-gray-200 transition">
                          {openRowIndex === idx ? (
                            <ChevronUp className="w-5 h-5 text-gray-600" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-600" />
                          )}
                        </div>
                      </td>
                    )}

                    <td className="px-3 py-2">{row.exbondNumber}</td>
                    <td className="px-3 py-2">{formatIndianNumber(row.total_weight)}</td>
                    <td className="px-3 py-2">{formatIndianNumber(row.total_duty_exbond_amount_inr)}</td>
                    {/* <td className="px-3 py-2">{row.total_invoice_amount_inr}</td> */}
                    <td
                      className="px-3 py-2 text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ActionDropdown
                        actions={{ edit: true, delete: true }}
                        onEdit={() => handleEdit(row.id, 0)}
                        onDelete={() => handleDeleteParent(row.id)}
                      />
                    </td>
                  </tr>

                  {/* Sub-table */}
                  {enableToggle &&
                    openRowIndex === idx &&
                    row.history.length > 0 && (
                      <tr>
                        <td></td>
                        <td colSpan={12}>
                          <div className="mt-2 flex justify-center mb-4">
                            <div className="inline-block w-[100%] overflow-hidden">
                              <div className="overflow-x-auto">
                                <table className="w-full text-[11px] text-center ">
                                  <thead className="text-gray-600 font-medium border-b">
                                    <tr>
                                      <th className="px-3 py-2">Resultant</th>
                                      <th className="px-3 py-2">BI No.</th>
                                      <th className="px-3 py-2">BE No.</th>
                                      <th className="px-3 py-2">BE Date</th>
                                      <th className="px-3 py-2">Section</th>
                                      <th className="px-3 py-2">Duty Ex-bond (₹)</th>
                                      <th className="px-3 py-2">Weight (kg)</th>
                                      <th className="px-3 py-2">Invoice Value</th>
                                      <th className="px-3 py-2">Customer</th>
                                      <th className="px-3 py-2">Type</th>
                                      <th className="px-3 py-2">Dollar</th>
                                      <th className="px-3 py-2">Rate (₹)</th>
                                      <th className="px-3 py-2">Duty Paid</th>
                                      <th className="px-3 py-2">Action</th>
                                    </tr>
                                  </thead>

                                  <tbody className="divide-y divide-gray-100">
                                    {row.history.map((h, i) => {
                                      const color = colorPairs[h.colorIndex];
                                      return (
                                        <tr key={i} className="hover:bg-gray-50">
                                          <td className="px-2 py-1">
                                            <div
                                              className="w-14 h-6 flex items-center justify-center font-semibold text-xs text-center rounded uppercase shadow-sm mx-auto"
                                              style={{
                                                backgroundColor: color.bg,
                                                color: color.text,
                                              }}
                                            >
                                              {h.material_short_code}
                                            </div>
                                          </td>
                                          <td className="px-2 py-1">{h.biNumber.toUpperCase()}</td>
                                          <td className="px-2 py-1">{h.beNumber}</td>
                                          <td className="px-2 py-1">{formatDate(h.be_date)}</td>
                                          <td className="px-2 py-1">{h.section ? h.section.charAt(0).toUpperCase() + h.section.slice(1) : ""}</td>
                                          <td className="px-2 py-1">{formatIndianNumber(h.dutyExBond)}</td>
                                          <td className="px-2 py-1">{formatIndianNumber(h.weight)}</td>
                                          <td className="px-2 py-1"> {formatIndianNumber(h.invoiceValue)}</td>
                                          <td className="px-2 py-1">{h.customer ? h.customer.charAt(0).toUpperCase() + h.customer.slice(1) : ""}</td>
                                          <td className="px-2 py-1">{h.type ? h.type.charAt(0).toUpperCase() + h.type.slice(1) : ""}</td>
                                          <td className="px-2 py-1"> {formatIndianNumber(h.dollar)}</td>
                                          <td className="px-2 py-1">₹ {formatIndianNumber(h.rate)}</td>
                                          <td className="px-2 py-1">{h.dutyPaid}</td>
                                          <td className="px-2 py-1">
                                            <div className="flex items-center justify-center space-x-1">
                                              <button
                                                className="text-green-500 hover:text-green-700 p-1"
                                                title="Edit Entry"
                                                id={row.history[i]?.id}
                                                onClick={() => handleEdit(row.id, row.history[i]?.id)}
                                              >
                                                <MdEditSquare size={18} />
                                              </button>
                                              <button
                                                className="text-red-500 hover:text-red-700 p-1"
                                                title="Delete Entry"
                                                onClick={() => handleDeleteChild(row.id, h.id)}
                                              >
                                                <RiDeleteBin5Line size={18} />
                                              </button>
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}

                  <tr>
                    <td colSpan={10} className="border-b border-dashed border-gray-300"></td>
                  </tr>
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Modals */}
      {/* Delete preview modal for parent or child */}
      {showDeletePreview && pendingDeleteData && (
        <DeletePreviewModal
          title={deleteMode === "parent" ? "Exbond Details" : "Child Entry Details"}
          data={
            deleteMode === "parent"
              ? {
                "Exbond No.": pendingDeleteData.parent?.exbondNumber,
                "Total Exbond Weight": pendingDeleteData.parent?.total_weight,
                "Total Duty Ex-bond": pendingDeleteData.parent?.total_duty_exbond_amount_inr,
              }
              : {
                "Material": pendingDeleteData.child?.material_short_code.toUpperCase(),
                "BI No.": pendingDeleteData.child?.biNumber.toUpperCase(),
                "BE No.": pendingDeleteData.child?.beNumber,
                "BE Date": formatDate(pendingDeleteData.child.be_date),
                "Duty Ex-bond": pendingDeleteData.child?.dutyExBond,
                "Weight": pendingDeleteData.child?.weight,
                "Invoice Value": pendingDeleteData.child?.invoiceValue,
                "Customer": pendingDeleteData.child?.customer
                  ? pendingDeleteData.child?.customer
                    .toLowerCase()
                    .split(",") // split by comma only
                    .map(part =>
                      part
                        .trim()
                        .split(" ")
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(" ")
                    )
                    .join(", ") // add comma + space back
                  : "",
                "Type": pendingDeleteData.child?.type
                  ? pendingDeleteData.child?.type
                    .toLowerCase()
                    .split(",") // split by comma only
                    .map(part =>
                      part
                        .trim()
                        .split(" ")
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(" ")
                    )
                    .join(", ") // add comma + space back
                  : "",
                "Duty Paid": pendingDeleteData.child?.dutyPaid
                  ? pendingDeleteData.child?.dutyPaid
                    .toLowerCase()
                    .split(",") // split by comma only
                    .map(part =>
                      part
                        .trim()
                        .split(" ")
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(" ")
                    )
                    .join(", ") // add comma + space back
                  : "",
              }
          }
          onCancel={handleCancelPreview}
          onProceed={handleProceedToFinalConfirm}
        />
      )}

      {/* Final delete confirm modal */}
      {showFinalDeleteConfirm && pendingDeleteData && (
        <FinalDeleteConfirmModal
          title="Confirm Delete"
          message="Are you sure you want to permanently delete this entry?"
          onCancel={() => {
            setShowFinalDeleteConfirm(false);
            setPendingDeleteData(null);
            setDeleteMode(null);
          }}
          onConfirm={handleConfirmDelete}
        />
      )}
      {warningVisible && (
        <WarningModal
          message={warningMessage}
          onClose={() => setWarningVisible(false)}
        />
      )}
    </>
  );
}
